
import { icons } from 'ckeditor5/src/core';
import { ButtonView, FocusCycler, LabeledFieldView, View, ViewCollection, submitHandler, addListToDropdown, createLabeledDropdown } from 'ckeditor5/src/ui';
import { FocusTracker, KeystrokeHandler } from 'ckeditor5/src/utils';
import PlaceholderInsertFormRowView from './placeholderinsertformrowview';
import '../theme/placeholderinsert.css';
import getDropdownItemsDefinitions from '../utils';

export default class PlaceholderInsertPanelView extends View {

    constructor(locale, config) {
        super(locale);
        
        this.config = config;

        this.placeholders = this.config.get('placeholderConfig.types');

        this.placeholderInputView = this._createPlaceholderInput(this.placeholders);
        

        const { insertButtonView, cancelButtonView } = this._createActionButtons(locale);
        this.insertButtonView = insertButtonView;
        this.cancelButtonView = cancelButtonView;

        this.set('placeholderInputValue', '');
        
        this.focusTracker = new FocusTracker();
        this.keystrokes = new KeystrokeHandler();
        this._focusables = new ViewCollection();
        this._focusCycler = new FocusCycler({
            focusables: this._focusables,
            focusTracker: this.focusTracker,
            keystrokeHandler: this.keystrokes,
            actions: {
                // Navigate form fields backwards using the Shift + Tab keystroke.
                focusPrevious: 'shift + tab',
                // Navigate form fields forwards using the Tab key.
                focusNext: 'tab'
            }
        });

        
        this.setTemplate({
            tag: 'form',
            attributes: {
                class: [
                    'ck',
                    'ck-placeholder-insert-form'
                ],
                tabindex: '-1'
            },
            children: [
                this.placeholderInputView,
                new PlaceholderInsertFormRowView(locale, {
                    children: [
                        this.insertButtonView,
                        this.cancelButtonView
                    ],
                    class: 'ck-placeholder-insert-form__action-row'
                })
            ]
        });
    }
    /**
     * @inheritDoc
     */
    render() {
        super.render();
        submitHandler({
            view: this
        });
        const childViews = [
            this.placeholderInputView,
            this.insertButtonView,
            this.cancelButtonView
        ];
        childViews.forEach(v => {
            // Register the view as focusable.
            this._focusables.add(v);
            // Register the view in the focus tracker.
            this.focusTracker.add(v.element);
        });
        // Start listening for the keystrokes coming from #element.
        this.keystrokes.listenTo(this.element);
        const stopPropagation = (data) => data.stopPropagation();
        // Since the form is in the dropdown panel which is a child of the toolbar, the toolbar's
        // keystroke handler would take over the key management in the URL input. We need to prevent
        // this ASAP. Otherwise, the basic caret movement using the arrow keys will be impossible.
        this.keystrokes.set('arrowright', stopPropagation);
        this.keystrokes.set('arrowleft', stopPropagation);
        this.keystrokes.set('arrowup', stopPropagation);
        this.keystrokes.set('arrowdown', stopPropagation);
    }

    /**
     * Focuses the first {@link #_focusables focusable} in the form.
     */
    focus() {
        this._focusCycler.focusFirst();
    }
    /**
    * The native DOM `value` of the {@link #placeholderInputView} element.
    *
    */
    get placeholder() {
        return this.placeholderInputValue.trim();
    }
    set placeholder(value) {

        this.placeholderInputValue = value.trim();

        const placeholder = this.placeholders.find(e => e.value === this.placeholderInputValue);

        this.placeholderInputView.label = placeholder?.text;

    }
    /**
     * Validates the form and returns `false` when some fields are invalid.
     */
    isValid() {
        this.resetFormStatus();
        
        const errorText = null;
        // One error per field is enough.
        if (errorText) {
            // Apply updated error.
            this.placeholderInputView.errorText = errorText;
            return false;
        }
        
        return true;
    }
    /**
     * Cleans up the supplementary error and information text of the {@link #placeholderInputView}
     * bringing them back to the state when the form has been displayed for the first time.
     *
     * See {@link #isValid}.
     */
    resetFormStatus() {
        this.placeholderInputView.errorText = null;
    }
    /**
     * @inheritDoc
     */
    destroy() {
        super.destroy();
        this.focusTracker.destroy();
        this.keystrokes.destroy();
    }
    /**
     * Creates the following form controls:
     *
     * * {@link #insertButtonView},
     * * {@link #cancelButtonView}.
     *
     * @param locale The localization services instance.
     */
    _createActionButtons(locale) {
        const t = locale.t;
        const insertButtonView = new ButtonView(locale);
        const cancelButtonView = new ButtonView(locale);
        insertButtonView.set({
            label: t('Insert'),
            icon: icons.check,
            class: 'ck-button-save',
            type: 'submit',
            withText: true,
            isEnabled: this.placeholderInputValue
        });
        cancelButtonView.set({
            label: t('Cancel'),
            icon: icons.cancel,
            class: 'ck-button-cancel',
            withText: true
        });
        insertButtonView.bind('isEnabled').to(this, 'placeholderInputValue', value => !!value);
        insertButtonView.delegate('execute').to(this, 'submit');
        cancelButtonView.delegate('execute').to(this, 'cancel');
        return { insertButtonView, cancelButtonView };
    }

    _createPlaceholderInput(placeholders) {

        const t = this.locale.t;

        const labeledInput = new LabeledFieldView(this.locale, createLabeledDropdown);


        const dropdownView = labeledInput.fieldView;

        // Populate the list in the dropdown with items.
        addListToDropdown( dropdownView, getDropdownItemsDefinitions(placeholders) );

        dropdownView.buttonView.set( {
            // The t() function helps localize the editor. All strings enclosed in t() can be
            // translated and change when the language of the editor changes.
            label: t( 'Select placeholder...' ),
            tooltip: true,
            withText: true,
            width: 300
        } );

        this._placeholderInputViewInfoDefault = t('Select a placeholder from the list.');
        
        //labeledInput.label = t('');
        labeledInput.infoText = this._placeholderInputViewInfoDefault;

        this.listenTo( dropdownView, 'execute', evt => {
            // Display the tip text only when there is some value. Otherwise fall back to the default info text.
            const sample = evt.source.commandParam.sample.trim();
            labeledInput.infoText = sample ? 'Example: ' + evt.source.commandParam.sample.trim() : '';
            this.placeholder = evt.source.commandParam.value.trim();
        } );

        //dropdownView.on('input', () => {
            // Display the tip text only when there is some value. Otherwise fall back to the default info text.
        //    labeledInput.infoText = inputField.element.value ? this._placeholderInputViewInfoTip : this._placeholderInputViewInfoDefault;
        //    this.placeholderInputValue = dropdownView.element.value.trim();
        //});
        return labeledInput;
    }

}
